import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Col,
  DatePicker,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
/* eslint-disable no-nested-ternary */
import PropTypes from "prop-types";

import { DownloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import ManagePlanModal from "../../Components/ManagePlanModal";
import ManageTagUntagPartnerModal from "../../Components/ManageTagUntagPartnerModal";
import RefreshEarningsButton from "../../Components/RefreshEarningsButton/RefreshEarningsButton";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import {
  DATE_TIME_FORMAT,
  DEFAULT_DATES_RANGE_OPTIONS,
} from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import { numFormatter } from "../../Utils/formatter";
import debounce from "../../Utils/utilityFunctions";
import { getTagmangoCreatorsData, upgradePlan } from "../api";
import CreatorDetailExpand from "./CreatorDetailExpand";

const { Option } = Select;

const TagmangoCreators = ({ sensitiveAllowedActions }) => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "month").startOf("day")
  );
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  const [earningGt, setEarningGt] = useState(null);
  const [earningLt, setEarningLt] = useState(null);
  const [mangoCountGt, setMangoCountGt] = useState(null);
  const [mangoCountLt, setMangoCountLt] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [planUpgradeModalVisible, setPlanUpgradeModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tableKey, setTableKey] = useState(Date.now());
  const [managePlanModalVisible, setManagePlanModalVisible] = useState(false);
  const [partnerTagUntagModalVisible, setPartnerTagUntagModalVisible] =
    useState(false);

  const controllerRef = useRef(null);

  const sensitiveAllowedFunctionSet = React.useMemo(
    () => new Set(sensitiveAllowedActions),
    [sensitiveAllowedActions]
  );

  const creatorsTable = () => {
    const columns = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          return (
            <TableUserInfoItem
              name={record?.name}
              profilePic={record?.profilePicUrl}
              hideContact
            />
          );
        },
      },
      {
        title: "Mango Count",
        dataIndex: "mangoCount",
        key: "mangoCount",
      },
      {
        title: "Total Earnings",
        dataIndex: "totalEarning",
        key: "totalEarning",
        render: (amount) => numFormatter({ amount }),
      },
      {
        title: "Created On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (text) => moment(text).format(DATE_TIME_FORMAT),
      },
      {
        title: "Actions",
        key: "actions",
        align: "end",
        render: (_, record) => {
          if (!record?.whitelabelPlanType) {
            return (
              <Space size="small" direction="horizontal" align="end">
                <Button
                  className="siteBtn darkBtn"
                  onClick={() => {
                    setSelectedUser(record);
                    setPlanUpgradeModalVisible(true);
                  }}
                  disabled
                >
                  Upgrade plan
                </Button>
                {sensitiveAllowedFunctionSet.has("managePlanButton") ? (
                  <Button
                    className="siteBtn accentBtnV1"
                    onClick={() => {
                      setSelectedUser(record);
                      setManagePlanModalVisible(true);
                    }}
                  >
                    Manage plan
                  </Button>
                ) : null}
                <Button
                  className="siteBtn accentBtnV1"
                  onClick={() => {
                    setSelectedUser(record);
                    setPartnerTagUntagModalVisible(true);
                  }}
                  block
                >
                  Tag/Untag partner
                </Button>
              </Space>
            );
          }

          const isEnterpriseOrFreedomPlan = [
            "enterprise-plan",
            "freedom-plan",
          ].includes(record?.whitelabelPlanType);

          if (!isEnterpriseOrFreedomPlan) {
            return (
              <Space size="small" direction="horizontal" align="end">
                {sensitiveAllowedFunctionSet.has("managePlanButton") ? (
                  <Button
                    className="siteBtn accentBtnV1"
                    onClick={() => {
                      setSelectedUser(record);
                      setManagePlanModalVisible(true);
                    }}
                  >
                    Manage plan
                  </Button>
                ) : null}

                <Button
                  className="siteBtn accentBtnV1"
                  onClick={() => {
                    setSelectedUser(record);
                    setPartnerTagUntagModalVisible(true);
                  }}
                  block
                >
                  Tag/Untag partner
                </Button>
              </Space>
            );
          }

          // if the user is on enterprise or freedom plan and is not migrated to White-Label, show the tag/untag partner button
          if (isEnterpriseOrFreedomPlan && !record.isMigratedToWhitelabel) {
            return (
              <Space size="small" direction="horizontal" align="end">
                <Button
                  className="siteBtn accentBtnV1"
                  onClick={() => {
                    setSelectedUser(record);
                    setPartnerTagUntagModalVisible(true);
                  }}
                  block
                >
                  Tag/Untag partner
                </Button>
              </Space>
            );
          }

          return null;
        },
      },
    ];

    return (
      <Table
        key={tableKey}
        columns={columns}
        dataSource={loading ? [] : users}
        pagination={{
          current: page,
          total,
          onChange: (p) => setPage(p),
        }}
        loading={loading}
        rowKey={(record) => record._id}
        expandable={{
          expandedRowRender: (record) => (
            <CreatorDetailExpand creatorId={record?._id} />
          ),
        }}
        locale={{
          emptyText: loading ? "Loading..." : "No users found",
        }}
      />
    );
  };

  const handleGetTagmangoCreators = async () => {
    setLoading(true);
    try {
      controllerRef.current = new AbortController();
      const resp = await getTagmangoCreatorsData(
        {
          search,
          startDate,
          endDate,
          earningGt,
          earningLt,
          mangoCountGt,
          mangoCountLt,
          page,
          limit: 10,
          csv: false,
        },
        { signal: controllerRef?.current?.signal }
      );
      if (resp.code === 0) {
        controllerRef.current = null;
        setUsers(resp?.result?.data);
        setTotal(resp?.result?.total);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      // message.error("Something went wrong");
      showAppError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetTagmangoCreatorsCSV = async () => {
    try {
      const resp = await getTagmangoCreatorsData({
        search,
        startDate,
        endDate,
        earningGt,
        earningLt,
        mangoCountGt,
        mangoCountLt,
        page,
        limit: 10,
        csv: true,
      });
      fileDownload(resp, `tagmango-creators-${Date.now()}.csv`);
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const handlePlanUpgrade = async (userId, planType) => {
    const hide = message.loading("Upgrading plan...");
    try {
      const resp = await upgradePlan({ userId, planType });
      if (resp.code === 0) {
        message.success("Plan upgraded successfully");
        setPlanUpgradeModalVisible(false);
        setSelectedUser(null);
        setSelectedPlan(null);
        handleGetTagmangoCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  useEffect(() => {
    const controller = controllerRef.current;
    if (controller?.abort) {
      controller?.abort();
    }
    handleGetTagmangoCreators();
  }, [
    search,
    startDate,
    endDate,
    earningGt,
    earningLt,
    mangoCountGt,
    mangoCountLt,
    page,
  ]);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 12,
        }}
      >
        <Col>
          <CustomSearchbar
            placeholder="Search by name, email or phone"
            searchQuery={search}
            setSearchQuery={(value) => {
              setSearch(value);
              setPage(1);
            }}
            style={{ width: 500 }}
          />
        </Col>
        <Col style={{ marginLeft: "auto" }}>
          <Space>
            <RefreshEarningsButton />
            <Button
              className="siteBtn grayBtn"
              onClick={handleGetTagmangoCreatorsCSV}
            >
              <DownloadOutlined />
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
        wrap
      >
        <Col span={8}>
          <Space>
            <Typography>Amount Made: </Typography>
            <InputNumber value={earningGt} onChange={debounce(setEarningGt)} />
            <Typography>To:</Typography>
            <InputNumber value={earningLt} onChange={debounce(setEarningLt)} />
          </Space>
        </Col>
        <Col span={8}>
          <Space>
            <Typography>Number of Mangoes Made: </Typography>
            <InputNumber
              value={mangoCountGt}
              onChange={debounce(setMangoCountGt)}
            />
            <Typography>To:</Typography>
            <InputNumber
              value={mangoCountLt}
              onChange={debounce(setMangoCountLt)}
            />
          </Space>
        </Col>
        <Col
          span={8}
          style={{
            height: "100%",
          }}
        >
          <Space>
            <Typography>Created On: </Typography>
            <DatePicker.RangePicker
              size="large"
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              value={[startDate, endDate]}
              onChange={(values) => {
                setStartDate(values?.[0]);
                setEndDate(values?.[1]);
              }}
            />
          </Space>
        </Col>
      </Row>

      {creatorsTable()}

      <ManagePlanModal
        visible={managePlanModalVisible}
        onClose={() => setManagePlanModalVisible(false)}
        onSave={() => {
          handleGetTagmangoCreators();
          setManagePlanModalVisible(false);
        }}
        userId={selectedUser?._id}
        mode="normal"
      />

      <ManageTagUntagPartnerModal
        visible={partnerTagUntagModalVisible}
        onClose={() => setPartnerTagUntagModalVisible(false)}
        onSave={() => {
          setPartnerTagUntagModalVisible(false);
        }}
        userId={selectedUser?._id}
      />

      <Modal
        title="Upgrade Plan"
        visible={planUpgradeModalVisible}
        onCancel={() => setPlanUpgradeModalVisible(false)}
        footer={null}
      >
        <Typography>
          Are you sure you want to upgrade the plan for {selectedUser?.name}?
        </Typography>
        <Space
          direction="vertical"
          style={{
            width: "100%",
          }}
        >
          <Select
            style={{
              width: "200px",
            }}
            value={selectedPlan}
            onChange={(value) => setSelectedPlan(value)}
          >
            <Option value="freedom-plan">Freedom Plan</Option>
            <Option value="enterprise-plan">Enterprise Plan</Option>
          </Select>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (selectedPlan) {
                  handlePlanUpgrade(selectedUser?._id, selectedPlan);
                } else {
                  message.error("Please select a plan");
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setPlanUpgradeModalVisible(false);
                setSelectedUser(null);
                setSelectedPlan(null);
              }}
            >
              No
            </Button>
          </Space>
        </Space>
      </Modal>
    </>
  );
};

TagmangoCreators.propTypes = {
  sensitiveAllowedActions: PropTypes.shape([]),
};

export default TagmangoCreators;
